import {
  INTEGER,
  TEXT,
  readingCompensation,
  readingCompleted,
  readingCompletedTimestamp,
  readingContent,
  readingDateLabel,
  readingEntryCompleted,
  readingEntryId,
  readingId,
  readingPlanId,
  readingSequence,
  userId,
} from '../constants/stringsAndFields';
import {isNotEmpty, removeDuplicates} from '../utils/arrays';
import {
  SQL_TIME_NOW,
  checkTableExists,
  deleteData,
  executeTransaction,
  insertData,
  selectData,
  selectDataMultiWhere,
  selectDataTripleWhere,
  updateData,
} from './common';
import {getActiveUserId} from './currentUser';
import {READING_PLAN_TABLE} from './readingPlan';
import {APP_USER_TABLE} from './user';
import {LOCAL_DEFAULT_USER_ID} from './userConstants';
import {runSqlCmd} from './sqlOperations';
import {exportDatabase} from './sqlOperations';

export const READING_PLAN_PROGRESS_TABLE = 'ReadingPlanProgress';

const keys = [
  readingId,
  userId,
  readingPlanId,
  readingEntryId,
  readingContent,
  readingSequence,
  readingCompleted,
  readingCompletedTimestamp,
  readingEntryCompleted,
  readingCompensation,
  readingDateLabel,
];

const defaultUserIdSql = `'${LOCAL_DEFAULT_USER_ID}'`;

// TODO: add index which is used for query
export const createReadingPlanProgressTable = async tx => {
  await runSqlCmd(tx, `DROP TABLE IF EXISTS ${READING_PLAN_PROGRESS_TABLE};`);
  await runSqlCmd(
    tx,
    `CREATE TABLE IF NOT EXISTS ${READING_PLAN_PROGRESS_TABLE}(
      ${readingId} ${TEXT} PRIMARY KEY NOT NULL,
      ${userId} ${TEXT} NOT NULL DEFAULT ${defaultUserIdSql},
      ${readingPlanId} ${INTEGER},
      ${readingEntryId} ${INTEGER},
      ${readingContent} ${TEXT},
      ${readingSequence} ${INTEGER},
      ${readingCompleted} ${INTEGER} DEFAULT 0,
      ${readingCompletedTimestamp} ${INTEGER},
      ${readingEntryCompleted} ${INTEGER} DEFAULT 0,
      ${readingCompensation} ${INTEGER} DEFAULT 0,
      ${readingDateLabel} ${TEXT},
      FOREIGN KEY(${userId}) REFERENCES ${APP_USER_TABLE}(${userId}) ON DELETE CASCADE,
      FOREIGN KEY(${readingPlanId}) REFERENCES ${READING_PLAN_TABLE}(${readingPlanId}) ON UPDATE NO ACTION ON DELETE NO ACTION);`,
  );
};

export const reCreateReadingPlanProgressTableAndData = async (tx, cmds) => {
  await createReadingPlanProgressTable(tx);
  await executeTransaction(tx, cmds);
};

export const insertReadingPlanProgress = async (tx, readingPlan) => {
  const row = {
    ...readingPlan,
    [userId]: readingPlan[userId] ?? getActiveUserId(),
  };
  await insertData(tx, READING_PLAN_PROGRESS_TABLE, keys, row);
};

export const deleteAllReadingPlanProgress = async tx => {
  await runSqlCmd(
    tx,
    `DELETE FROM ${READING_PLAN_PROGRESS_TABLE} WHERE ${userId} = '${getActiveUserId()}';`,
  );
};

export const deleteReadingPlanProgress = async (tx, readingPlanProgress) => {
  await deleteData(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    readingPlanProgress[readingId],
  );
};

export const updateReadingPlanProgress = async (tx, readingPlanProgress) => {
  await updateData(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    readingPlanProgress,
    readingPlanProgress[readingId],
  );
};

export const getReadingPlanProgresses = async tx => {
  const readingPlanProgresses = await selectData(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    keys,
    userId,
    getActiveUserId(),
  );
  return isNotEmpty(readingPlanProgresses) ? readingPlanProgresses : [];
};

export const getReadingPlanProgress = async (tx, dataId) => {
  const readingPlanProgresses = await selectDataMultiWhere(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    keys,
    [readingId, userId],
    [dataId, getActiveUserId()],
    [],
    null,
  );
  return isNotEmpty(readingPlanProgresses) ? readingPlanProgresses[0] : null;
};

export const checkReadingPlanProgressTable = async tx => {
  return await checkTableExists(tx, READING_PLAN_PROGRESS_TABLE);
};

export const getDailyReadings = async (tx, readingPlanIdValue) => {
  const dailyReadings = await selectDataMultiWhere(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    keys,
    [readingPlanId, userId],
    [readingPlanIdValue, getActiveUserId()],
    [readingSequence, 'ASC'],
    true,
  );
  const dailyReadingsWithoutDuplicates = removeDuplicates(
    dailyReadings,
    readingEntryId,
  );
  return dailyReadingsWithoutDuplicates;
};

export const getDailyReading = async (
  tx,
  readingPlanIdValue,
  readingEntryIdValue,
) => {
  const dailyReadings = await selectDataTripleWhere(
    tx,
    READING_PLAN_PROGRESS_TABLE,
    keys,
    [readingPlanId, readingEntryId, userId],
    [readingPlanIdValue, readingEntryIdValue, getActiveUserId()],
    [readingSequence, 'ASC'],
    false,
  );
  return dailyReadings;
};

export const resetReadingPlan = async (tx, readingPlanIdValue) => {
  const uid = getActiveUserId();
  await runSqlCmd(
    tx,
    `UPDATE ${READING_PLAN_PROGRESS_TABLE} SET ${readingCompleted} = 0, ${readingEntryCompleted} = 0 WHERE ${readingPlanId} = '${readingPlanIdValue}' AND ${userId} = '${uid}';`,
  );
  await exportDatabase(tx);
};

export const setReadingCompletedAndSave = async (tx, data, isCompleted) => {
  data[readingCompleted] = isCompleted;
  data[readingCompletedTimestamp] = SQL_TIME_NOW;
  await updateData(tx, READING_PLAN_PROGRESS_TABLE, data, readingId);
  await updateAllReadingEntryCompletition(
    tx,
    data[readingPlanId],
    data[readingEntryId],
  );
  await exportDatabase(tx);
};

export const updateAllReadingEntryCompletition = async (
  tx,
  readingPlanIdValue,
  readingEntryIdValue,
) => {
  const readings = await getDailyReading(
    tx,
    readingPlanIdValue,
    readingEntryIdValue,
  );
  // update whole day entry completed if all readings are read
  let entryReadingCompleted = true;

  for (let reading of readings) {
    if (!reading[readingCompleted]) {
      entryReadingCompleted = false;
      break;
    }
  }
  for (let reading of readings) {
    reading[readingEntryCompleted] = entryReadingCompleted ? true : false;
    await updateData(tx, READING_PLAN_PROGRESS_TABLE, reading, readingId);
  }
};
